import { CountBadge } from '@vrplatform/components';
import type { Query } from '@vrplatform/data';
import { useEnabledFeatures, useSubscription, useTeam } from '@vrplatform/data';
import { Icon } from '@vrplatform/icons';
import { IconButton } from '@vrplatform/components-legacy';
import { Stack, Title, Tooltip } from '@mantine/core';
import { useNavigate } from 'react-router';
import { NavbarRoute } from '../layout/navbar';
import {
  getMissingAccountAssignments,
  useVrpRetoolQuery,
} from '@vrplatform/data';
import { PartnerConnectionErrorAggregateBadge } from '../views/partner-connections/PartnerConnectionErrorAggregateBadge';

const iconSize = 18;

export function VrpNavigation() {
  const goto = useNavigate();

  const { data: vrpRetoolViews } = useVrpRetoolQuery();

  return (
    <>
      <NavbarRoute
        title="Overview"
        link={'/dashboard'}
        icon={<Icon icon="Activity2Icon" size={iconSize} />}
      />

      <Stack gap="xs" mb="sm">
        <NavbarRoute
          title="Statements"
          link={'/team-statements'}
          icon={<Icon icon="ClipboardTextIcon" size={iconSize} />}
        />
        <NavbarRoute
          title="Automations"
          link={'/team-automations'}
          icon={<Icon icon="RocketIcon" size={iconSize} />}
        />

        <NavbarRoute
          title="Connections"
          link={'/team-connections?status=active'}
          icon={<Icon icon="ShareIcon" size={iconSize} />}
          notification={
            <IconButton
              onClick={(event) => {
                event.preventDefault();
                event.stopPropagation();
                goto('/team-connections/errors?status=active');
              }}
              sx={{
                width: 'unset',
              }}
            >
              <PartnerConnectionErrorAggregateBadge loadingColor="white" />
            </IconButton>
          }
        />
      </Stack>

      <Stack gap="xs" mb="xl">
        <Title order={5} c="white" fw={400} ml={8}>
          VRP Admin
        </Title>

        <NavbarRoute
          title="Billing"
          link={'/billing'}
          icon={<Icon icon="CircleDollarIcon" size={iconSize} />}
        />
        <NavbarRoute
          title="Property Managers"
          link={'/property-manager'}
          icon={<Icon icon="HomeIcon" size={iconSize - 1} />}
        />
        <NavbarRoute
          title="Partners"
          link={'/partner'}
          icon={<Icon icon="OfficeIcon" size={iconSize} />}
        />
        <NavbarRoute
          title="Users"
          link={'/users'}
          icon={<Icon icon="UsersIcon" size={iconSize - 1} />}
        />
        <NavbarRoute
          title="Administrators"
          link={'/members'}
          icon={<Icon icon="UsersIcon" size={iconSize} />}
        />
      </Stack>

      <Stack gap="xs">
        <Title order={5} c="white" fw={400} ml={8}>
          Templates
        </Title>

        <NavbarRoute
          title="Chart of Accounts"
          link={'/accounts'}
          icon={<Icon icon="BriefcaseIcon" size={iconSize} />}
          notification={<ChartOfAccountIssueBadge />}
        />
        <NavbarRoute
          title="Line Mappings"
          link={'/line-mappings'}
          icon={<Icon icon="BookOpenIcon" size={iconSize} />}
        />
        <NavbarRoute
          title="Booking Channels"
          link={'/booking-channels'}
          icon={<Icon icon="BookingComIcon" size={iconSize} />}
        />
        <NavbarRoute
          title="Statement Template"
          link={'/statements/templates'}
          icon={<Icon icon="FileInvoiceIcon" size={iconSize} />}
          notification={<ChartOfAccountIssueBadge />}
        />
        <NavbarRoute
          title="Fees & Commissions"
          link="/fees-commissions"
          icon={<Icon icon="PercentageIcon" size={iconSize} />}
        />
      </Stack>

      {!!vrpRetoolViews?.length && (
        <Stack mt="md" gap="xs">
          {vrpRetoolViews.map((view) => (
            <NavbarRoute
              key={view.id}
              title={view.title}
              link={`/retool/${view.id}`}
              icon={<Icon icon="FileTextIcon" size={iconSize} />}
            />
          ))}
        </Stack>
      )}
    </>
  );
}

const ChartOfAccountIssueBadge = () => {
  const [{ id: teamId }] = useTeam();
  const { OPEX } = useEnabledFeatures();

  const { data, isLoading } = useSubscription(
    (q, args) => {
      const count = getMissingAccountAssignments(q as Query, {
        teamId: args.teamId,
        OPEX: args.OPEX,
      }).length;

      return count;
    },
    { teamId, OPEX }
  );

  const count = data || 0;

  if (isLoading) return <CountBadge count={0} loading loadingColor="white" />;

  if (!count) return null;

  return (
    <Tooltip
      label={`${count} missing account assignments`}
      withArrow
      disabled={!count}
    >
      <CountBadge count={count} loading={isLoading} loadingColor="white" />
    </Tooltip>
  );
};
